import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
// Render isolated HTML in a sandboxed iframe so that page/app CSS does not affect it,
// and the HTML can carry its own <style> safely (scripts are blocked by sandbox).
const SafeHtmlBlock = ({ html, className, style, sandbox }) => {
    const ref = React.useRef(null);
    const [height, setHeight] = React.useState(60);
    const uidRef = React.useRef(() => Math.random().toString(36).slice(2));
    if (typeof uidRef.current === 'function')
        uidRef.current = uidRef.current();
    const setDoc = React.useCallback(() => {
        const iframe = ref.current;
        if (!iframe)
            return;
        try {
            const uid = uidRef.current;
            const measure = `<script>(function(){
  var _raf=null; function schedule(){ try{ if(_raf) cancelAnimationFrame(_raf); _raf=requestAnimationFrame(send); }catch(e){} }
  function send(){
    try{
      var b=document.body, de=document.documentElement;
      var h=Math.max(
        b&&b.scrollHeight||0,
        de&&de.scrollHeight||0,
        b&&b.offsetHeight||0,
        de&&de.offsetHeight||0,
        b&&b.clientHeight||0,
        de&&de.clientHeight||0
      );
      parent.postMessage({__safeHtml:true, uid:'${uid}', h:h}, '*');
    }catch(e){}
  }
  document.addEventListener('DOMContentLoaded', send, {passive:true});
  window.addEventListener('load', send, {passive:true});
  setTimeout(send,0);
  // Observe size and DOM mutations
  try{ if('ResizeObserver' in window){
    try{ new ResizeObserver(schedule).observe(document.documentElement); }catch(e){}
    try{ new ResizeObserver(schedule).observe(document.body); }catch(e){}
  } }catch(e){}
  try{ if('MutationObserver' in window){ new MutationObserver(schedule).observe(document.documentElement, {subtree:true,childList:true,attributes:true}); } }catch(e){}
  // Listen to common UI events and transitions to re-measure
  ['transitionend','animationend','click','input','change'].forEach(function(ev){ try{ document.addEventListener(ev, schedule, true); }catch(e){} });
  // Fallback ticker
  try{ setInterval(send, 1200); }catch(e){}
})();</script>`;
            const isFull = /<!doctype|<\s*html[\s>]/i.test(String(html || ''));
            if (isFull) {
                // Inject measurement + transparent background for full documents
                let doc = String(html || '');
                const transparentStyle = '<style>html,body{background:transparent !important;}</style>';
                if (/<head[^>]*>/i.test(doc))
                    doc = doc.replace(/<head([^>]*)>/i, `<head$1>${transparentStyle}`);
                else if (/<html[^>]*>/i.test(doc))
                    doc = doc.replace(/<html([^>]*)>/i, `<html$1>${transparentStyle}`);
                else
                    doc = transparentStyle + doc;
                if (/<\/body>/i.test(doc))
                    doc = doc.replace(/<\/body>/i, measure + '</body>');
                else
                    doc = doc + measure;
                iframe.srcdoc = doc;
            }
            else {
                // Wrap fragment as a full doc + transparent background
                const payload = `<!doctype html><html><head><meta charset=\"utf-8\"><style>html,body{background:transparent !important;}</style></head><body>${html}
${measure}
</body></html>`;
                iframe.srcdoc = payload;
            }
        }
        catch { }
    }, [html]);
    const resize = React.useCallback(() => {
        const iframe = ref.current;
        if (!iframe)
            return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc)
                return;
            const h = Math.max(doc.body?.scrollHeight || 0, doc.documentElement?.scrollHeight || 0);
            if (h && Math.abs(h - height) > 2)
                setHeight(h);
        }
        catch { }
    }, [height]);
    React.useEffect(() => {
        setDoc();
    }, [setDoc]);
    React.useEffect(() => {
        const handler = (e) => {
            const iframe = ref.current;
            if (!iframe)
                return;
            if (!e || !e.data || e.source !== iframe.contentWindow)
                return;
            const d = e.data;
            if (d && d.__safeHtml && d.uid === uidRef.current && typeof d.h === 'number') {
                if (Math.abs(d.h - height) > 2)
                    setHeight(d.h);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [height]);
    return (_jsx("iframe", { ref: ref, className: className, style: { width: '100%', border: 'none', display: 'block', height, background: 'transparent', ...style }, sandbox: sandbox, onLoad: () => {
            resize();
        }, title: "html-block" }));
};
export default React.memo(SafeHtmlBlock);
